/**
 * @description A passkey as returned by the user-auth service, reduced to the
 * fields needed for device matching.
 */
export interface DevicePasskey {
    name: string
    id: string | number
    created_at: string | null
}

/**
 * @description Filter a list of passkeys down to those whose name matches the
 * current device/browser, inferred from the user agent. Pure: all device
 * inputs are passed in (sourced from `useDevice()` by the caller) so this can
 * be shared between the web and mobile-app composables without either
 * depending on the other.
 *
 * @param userAgent The current user agent string (`useDevice().userAgent`)
 * @param isIos Whether the device is iOS (`useDevice().isIos`)
 * @param isAndroid Whether the device is Android (`useDevice().isAndroid`)
 * @param passkeys The passkeys to filter
 */
export function getMatchingPasskeysOnDevice(
    userAgent: string,
    isIos: boolean,
    isAndroid: boolean,
    passkeys: DevicePasskey[]
): DevicePasskey[] {
    if (!passkeys?.length) return []

    const userAgentLower = userAgent.toLowerCase()
    const currentDeviceKeywords: string[] = []

    // Add platform keywords
    if (isIos) {
        currentDeviceKeywords.push('ios', 'iphone', 'ipad')
    }
    else if (isAndroid) {
        currentDeviceKeywords.push('android')
        // Add specific Android manufacturer if present
        if (/samsung/.test(userAgentLower)) currentDeviceKeywords.push('samsung')
        if (/pixel/.test(userAgentLower)) currentDeviceKeywords.push('pixel')
        if (/huawei/.test(userAgentLower)) currentDeviceKeywords.push('huawei')
        if (/xiaomi/.test(userAgentLower)) currentDeviceKeywords.push('xiaomi')
        if (/oppo/.test(userAgentLower)) currentDeviceKeywords.push('oppo')
    }
    else {
    // Desktop devices
        if (/mac os x/.test(userAgentLower)) currentDeviceKeywords.push('mac', 'macos')
        if (/windows/.test(userAgentLower)) currentDeviceKeywords.push('windows')
    }

    // Add browser keywords
    if (/chrome/.test(userAgentLower) && !/edg/.test(userAgentLower)) {
        currentDeviceKeywords.push('chrome')
    }
    if (/safari/.test(userAgentLower) && !/chrome/.test(userAgentLower)) {
        currentDeviceKeywords.push('safari')
    }
    if (/edg/.test(userAgentLower)) {
        currentDeviceKeywords.push('edge')
    }
    if (/firefox/.test(userAgentLower)) {
        currentDeviceKeywords.push('firefox')
    }

    if (!currentDeviceKeywords.length) return []

    // Word-boundary match so short markers like 'ios' don't false-match inside
    // unrelated words ('kiosk'). Test against the raw name AND a camelCase-split
    // copy ('iMac' -> 'i Mac') so short keywords embedded in camelCase device
    // names (e.g. 'mac' in 'iMac') are still caught, while multi-part keywords
    // ('iphone', 'ipad', 'macos') keep matching the raw form. Keywords are static
    // alnum literals, so no escaping is needed.
    const keywordMatchers = currentDeviceKeywords.map(k => new RegExp(`\\b${k}`))
    const splitCamelCase = (s: string) => s.replace(/([a-z])([A-Z])/g, '$1 $2')

    // Filter passkeys that match the current device
    return passkeys.filter((passkey) => {
        if (!passkey?.name) return false

        const raw = passkey.name.toLowerCase()
        const camel = splitCamelCase(passkey.name).toLowerCase()

        return keywordMatchers.some(re => re.test(raw) || re.test(camel))
    })
}

/**
 * @description Whether any of the given passkeys matches the current device.
 *
 * @param userAgent The current user agent string (`useDevice().userAgent`)
 * @param isIos Whether the device is iOS (`useDevice().isIos`)
 * @param isAndroid Whether the device is Android (`useDevice().isAndroid`)
 * @param passkeys The passkeys to check
 */
export function hasPasskeyOnDevice(
    userAgent: string,
    isIos: boolean,
    isAndroid: boolean,
    passkeys: DevicePasskey[]
): boolean {
    return getMatchingPasskeysOnDevice(userAgent, isIos, isAndroid, passkeys).length > 0
}
